import { Card } from '@/types/Card';
import {
  CARD_KEYS,
  CHANNEL_KEYS,
  CHANNEL_NAMES,
  BANK_KEYS,
  BANK_NAMES,
} from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.UBOT]: {
    sourceUrl: 'https://card.ubot.com.tw/Card?category=%E7%86%B1%E9%96%80%E6%8E%A8%E8%96%A6',
    lastUpdated: '2025-05-04',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // UBOT cards
  [CARD_KEYS.UBOT_MCARD]: {
    sourceUrl: 'https://activity.ubot.com.tw/2025Mcard/index.htm',
    applyUrl: 'https://card.ubot.com.tw/card_apply/?GID=39&IDE=C&openExternalBrowser=1',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.UBOT_JIHO]: {
    sourceUrl: 'https://card.ubot.com.tw/CardDetail/cardDetail202',
    applyUrl:
      'https://card.ubot.com.tw/eCard/dspCardOnLineApply.aspx?GID=59&IDE=A&strID=2008060014&PJN=JHA&UTC=&USC=2981778548',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.UBOT_LIANDIAN]: {
    sourceUrl: 'https://card.ubot.com.tw/CardDetail/cardDetail201',
    applyUrl: 'https://card.ubot.com.tw/card_apply/?GID=99&IDE=A&openExternalBrowser=1',
    lastUpdated: '2025-06-26',
  },
  [CARD_KEYS.UBOT_LINEBANK]: {
    sourceUrl: 'https://card.ubot.com.tw/CardDetail/cardDetail601',
    applyUrl:
      'https://activity.ubot.com.tw/LINEBank_Co-brandedCard/index.html?openExternalBrowser=1',
    lastUpdated: '2025-06-26',
  },
};

export const ubotCards: { [key: string]: Card } = {
  [CARD_KEYS.UBOT_JIHO]: {
    name: '聯邦吉鶴卡',
    summary: '日本消費最高4%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.UBOT],
    payment_network: 'JCB',
    picture: 'logo_ubot-jiho.png',
    key: 'ubot-jiho',
    link: cardLinkMap[CARD_KEYS.UBOT_JIHO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.UBOT_JIHO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '4% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '日本一般消費',
        condition:
          '日本一般消費使用聯邦吉鶴卡綁定 Apple Pay並於QUICPay場域感應交易，享活動加碼1.5%回饋(月上限1,000元)，最高4%回饋。',
        channel: '海外一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '基本回饋',
        condition: '基本回饋',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '海外一般消費',
        condition: '海外一般消費基本回饋',
        channel: '海外一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '4%',
    specific_channel_feedback: '4%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.UBOT_MCARD]: {
    name: '聯邦幸福M卡',
    summary: '交通旅遊最高6%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.UBOT],
    payment_network: 'VISA/Mastercard',
    picture: 'logo_ubot-mcard.png',
    key: 'ubot-mcard',
    link: cardLinkMap[CARD_KEYS.UBOT_MCARD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.UBOT_MCARD].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金回饋',
        max_feedback: '每月5,000元內享6%，超過享1%無上限',
        simplified_condition: '台鐵、高鐵、客運、租車',
        condition:
          '申辦電子帳單，台鐵、高鐵、指定客運(國光、統聯、和欣、阿羅哈、葛瑪蘭)、租車(聯邦租車、和運租車、格上租車)消費，每月5,000元內享6%回饋，超過享1%無上限',
        channel: '交通',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金回饋',
        max_feedback: '每月5,000元內享6%，超過享1%無上限',
        simplified_condition: '航空公司、訂房網、旅行社',
        condition:
          '申辦電子帳單，航空公司(華航、長榮、星宇等)、國內飯店、訂房網及旅遊平台(Booking.com、Agoda、Hotels.com等)、旅行社(東南、山富、五福、雄獅等)消費，每月5,000元內享6%回饋，超過享1%無上限',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外實體商店',
        condition: '申辦電子帳單，海外實體商店消費享3%回饋無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2% 現金回饋',
        max_feedback: '每戶最高回饋10,000元',
        simplified_condition: '保險費',
        condition:
          '申辦電子帳單，繳納保險費享1.2%回饋，每戶最高回饋10,000元，或可選擇12期0利率分期',
        channel: '保險',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '申辦電子帳單，國內一般消費享1%回饋無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '3%',
    specific_channel_feedback: '6%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.UBOT_LIANDIAN]: {
    name: '聯邦賴點卡',
    summary: '國內消費最高6.3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.UBOT],
    payment_network: 'VISA',
    picture: 'logo_ubot-liandian.png',
    key: 'ubot-liandian',
    link: cardLinkMap[CARD_KEYS.UBOT_LIANDIAN].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.UBOT_LIANDIAN].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6.3% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內消費最高回饋',
        condition: '國內消費最高6.3%回饋，包含基本回饋加上各項活動加碼回饋',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金',
        max_feedback: '每月上限依活動規定',
        simplified_condition: 'LINE Pay指定餐飲',
        condition: '綁定LINE Pay於指定餐飲消費享加碼5%回饋',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '5% 折扣',
        max_feedback: '天天現折',
        simplified_condition: '萊爾富超商',
        condition: '萊爾富超商消費天天現折5%',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.CONVENIENCE_STORE],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 現金',
        max_feedback: '每卡上限100元',
        simplified_condition: '一卡通自動加值',
        condition:
          '聯邦賴點卡新卡核卡後60日內，以一卡通全通路自動加值，且需於自動加值前或當月底前完成註冊並開通ipass money帳戶，享10% ipass money儲值金回饋',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.3% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '海外消費最高2.3%回饋',
        channel: '海外一般消費',
      },
    ],
    domestic_feedback: '6.3%',
    international_feedback: '2.3%',
    specific_channel_feedback: '6.3%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.UBOT_LINEBANK]: {
    name: 'LINE Bank聯名信用卡',
    summary: '免年費最高5.5%現金回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.UBOT],
    payment_network: 'VISA',
    picture: 'logo_ubot-linebank.png',
    key: 'ubot-linebank',
    link: cardLinkMap[CARD_KEYS.UBOT_LINEBANK].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.UBOT_LINEBANK].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '8.5% 現金',
        max_feedback: '依活動規定',
        simplified_condition: '指定消費最高回饋',
        condition: '指定消費回饋最高8.5%，包含基本回饋加上各項活動加碼回饋',
        channel: '指定消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金',
        max_feedback: '依活動規定',
        simplified_condition: 'LINE Pay綁定餐廳',
        condition: 'LINE Pay綁定餐廳消費享加碼5%回饋',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金',
        max_feedback: '單卡上限3次共450元',
        simplified_condition: '自動扣款成功',
        condition:
          '首次申辦LINE Bank聯名信用卡，核卡後180天內透過LINE Bank主帳戶自動扣繳信用卡款成功者，享每次扣繳成功扣款金額3%回饋',
        channel: '自動扣款',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國外消費',
        condition: '國外消費2.5%回饋無上限',
        channel: '海外一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內基本消費',
        condition: '國內基本消費不限通路2%現金回饋',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '2%',
    international_feedback: '2.5%',
    specific_channel_feedback: '8.5%',
    start_date: '2024-08-01',
    end_date: '2025-07-31',
  },
};
